import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { EMPTY, Observable } from "rxjs";

import { AsyncControls, AsyncStorage } from "@core/services/AsyncStorage";
import { Whitebox } from "@core/data/controller";
import { APIResponse } from '@shared/types.barrel';
import { ClientsService, Client } from '@admin/clients/clients.service';
import { DebugService as debug } from "@core/services/debug.service";
import { expand, map, reduce } from 'rxjs/operators';
import { AuthService } from '@core/services/auth/auth.service';

export { Whitebox };

interface WhiteboxListOutput {
  devices: Whitebox[];
  lastEvaluatedKey: string | null;
}

interface WhiteboxCustomConfiguration {
  configuration: Record<string, any>;
  createdOn: number | null;
  macAddress: string;
  modifiedOn: number | null;
}

@Injectable()
export class ControllersService {

  private _whiteboxes: AsyncStorage<Whitebox>;
  private _eTag: Record<string, string> = {};
  private _customConfigETag: Record<string, string> = {};

  private _activeClientId: string;
  private _lastUsedId: number = 0;


  constructor(
    private _http: HttpClient,
    private _clientsService: ClientsService,
    private _auth: AuthService
  ) {
    this._initializeStorage();
  }

  private _initializeStorage() {

    //Prepare location storage
    this._whiteboxes = new AsyncStorage(this._http);
    this._whiteboxes._setAddFunction(this._addWhitebox());
    this._whiteboxes._setElementComparisonFunction(this._whiteboxComparison);
    this._whiteboxes._setSaveActiveElementFunction(this._saveWhitebox());
    this._whiteboxes._setUpdateFunction(this._updateWhitebox());


    this._clientsService.clients.onActiveElementChange()
      .subscribe((client: Client) => {
        this._activeClientId = client.clientId;
        this._whiteboxes.update();
      });

  }

  public get whiteboxes() { return this._whiteboxes };

  public saveWhitebox(whitebox: Whitebox): Promise<Whitebox> {
    return new Promise((resolve, reject) => {
      this._saveWhitebox()(whitebox).subscribe((savedWhitebox: Whitebox) => {
        if (!savedWhitebox) {
          return reject(new Error("Unable to save whitebox."));
        }

        const mergedWhitebox = this._mergeWhiteboxes(whitebox, savedWhitebox);
        this._whiteboxes.update({ element: mergedWhitebox });
        return resolve(mergedWhitebox);
      }, reject)
    })
  };

  public getCustomConfiguration(whitebox: Whitebox): Observable<WhiteboxCustomConfiguration> {
    return this._http.get<APIResponse<WhiteboxCustomConfiguration>>(`whitebox/devices/${whitebox?.whiteboxId}/configuration`, { observe: "response" }).pipe(
      map((response: HttpResponse<APIResponse<WhiteboxCustomConfiguration>>) => {
        const body = response.body;
        if (body?.errors?.length) throw new Error(body.errors[0]);

        const configuration = body?.data;
        const eTag = response.headers?.get("ETag");
        if (eTag && whitebox?.whiteboxId) {
          this._customConfigETag[whitebox.whiteboxId] = eTag;
        }

        return configuration;
      })
    );
  }

  public saveCustomConfiguration(whitebox: Whitebox, configuration: string | Record<string, any>): Observable<WhiteboxCustomConfiguration> {
    const headers = {};
    if (whitebox?.whiteboxId && this._customConfigETag[whitebox.whiteboxId]) {
      headers["If-Match"] = this._customConfigETag[whitebox.whiteboxId];
    }

    const parsedConfiguration = typeof configuration === "string"
      ? JSON.parse(configuration)
      : configuration;

    return this._http.post<APIResponse<WhiteboxCustomConfiguration>>(
      `whitebox/devices/${whitebox?.whiteboxId}/configuration`,
      { configuration: parsedConfiguration },
      { headers, observe: "response" }
    ).pipe(
      map((response: HttpResponse<APIResponse<WhiteboxCustomConfiguration>>) => {
        const body = response.body;
        if (body?.errors?.length) throw new Error(body.errors[0]);

        const customConfiguration = body?.data;
        const eTag = response.headers?.get("ETag");
        if (eTag && whitebox?.whiteboxId) {
          this._customConfigETag[whitebox.whiteboxId] = eTag;
        }

        return customConfiguration;
      })
    );
  }

  private _whiteboxComparison(a: Whitebox, b: Whitebox): boolean {

    //Compare the two
    if (a == null || b == null) return false;
    if (a.whiteboxId && b.whiteboxId) return a.whiteboxId === b.whiteboxId;
    return a.macAddress === b.macAddress;

  }

  private _addWhitebox(): (whitebox: Whitebox, options?: any) => Observable<any> {

    let self = this;

    return (whitebox: Whitebox, options?: any): Observable<any> => {

      const requestData = {
        clientId: self._activeClientId || self._auth.activeClient?.clientId,
        expiry: whitebox.expiry || 30,
        locationId: whitebox.locationId,
        macAddress: whitebox.macAddress,
        nickname: whitebox.nickname,
        relays: Array.isArray(whitebox.relays) ? whitebox.relays : [],
        salesIds: whitebox.salesIds || {}
      };

      //Update or add the item
      return new Observable(observer => {
        this._http.post("whitebox/devices", requestData, { observe: "response" }).toPromise()
          .then((response: HttpResponse<APIResponse<Whitebox>>) => {
            const data = response.body;
            if (data?.errors?.length) {
              observer.error(data.errors[0]);
              return;
            }

            const result = data?.data;
            if (response.headers?.get("ETag") && result?.whiteboxId) {
              this._eTag[result.whiteboxId] = response.headers.get("ETag");
            }

            observer.next(result);
          })
          .catch(e => {
            const message = e?.error?.errors?.[0] || e?.error?.data?.message || e?.message || "Unknown error";
            observer.error(message);
          })
      })
    }

  }

  private _saveWhitebox(): (whitebox: Whitebox, options?: any) => Observable<any> {

    let self = this;

    return (whitebox: Whitebox, options?: any): Observable<any> => {

      const whiteboxId = whitebox?.whiteboxId;
      const headers = {};
      if (whiteboxId && self._eTag[whiteboxId]) {
        headers["If-Match"] = self._eTag[whiteboxId];
      }

      const updateData = {
        expiry: whitebox.expiry,
        locationId: whitebox.locationId,
        nickname: whitebox.nickname,
        relays: Array.isArray(whitebox.relays) ? whitebox.relays : [],
        salesIds: whitebox.salesIds || {}
      };

      for (let saleId in updateData.salesIds) {
        if (!updateData.salesIds[saleId].relay) updateData.salesIds[saleId].relay = []
      }

      //Update or add the item
      return new Observable(observer => {
        this._http.post(`whitebox/devices/${whiteboxId}`, updateData, { headers, observe: "response" }).toPromise()
          .then((response: HttpResponse<APIResponse<Whitebox>>) => {
            const updateResponse = response.body;
            if (updateResponse?.errors?.length) {
              observer.next(null);
            } else {
              const updatedWhitebox = updateResponse?.data;
              if (response.headers?.get("ETag") && updatedWhitebox?.whiteboxId) {
                self._eTag[updatedWhitebox.whiteboxId] = response.headers.get("ETag");
              }
              observer.next(updatedWhitebox);
            }
          })
          .catch(_ => { observer.next(null); })
      })
    }

  }

  private _updateWhitebox(): (options?: { element?: Whitebox }, asyncControls?: AsyncControls) => Observable<Whitebox[]> {
    let self = this;

    return (options: { element?: Whitebox } = {}, asyncControls?: AsyncControls) => {
      if (options?.element) {
        return new Observable((observer) => {
          const whiteboxes = self._whiteboxes.data.value || [];
          const index = whiteboxes.findIndex((whitebox) => self._whiteboxComparison(whitebox, options.element))
          if (index >= 0) whiteboxes[index] = self._mergeWhiteboxes(whiteboxes[index], options.element);
          observer.next(index >= 0 ? whiteboxes : [options.element, ...whiteboxes]);
        })
      }

      debug.log("Updating whiteboxes accounts");

      const clientId = self._activeClientId || self._auth.activeClient?.clientId;

      return this._http.get<APIResponse<WhiteboxListOutput>>("whitebox/devices", { params: { clientId } }).pipe(
        expand((response) => {
          if (response?.errors?.length) throw new Error(response.errors[0]);

          const lastEvaluatedKey = response?.data?.lastEvaluatedKey;
          if (!lastEvaluatedKey) return EMPTY;

          return this._http.get<APIResponse<WhiteboxListOutput>>("whitebox/devices", {
            params: { clientId, lastEvaluatedKey }
          });
        }),
        map((response) => response?.data?.devices || []),
        reduce((allWhiteboxes, currentWhiteboxes) => allWhiteboxes.concat(currentWhiteboxes), [] as Whitebox[]),
        map((whiteboxes: Whitebox[]) => {
          if (asyncControls?.activeClient)
            asyncControls.activeClient.next(clientId)

          whiteboxes.sort((leftSide: Whitebox, rightSide: Whitebox): number => {
            if (leftSide.nickname < rightSide.nickname) return -1
            if (leftSide.nickname > rightSide.nickname) return 1
            return 0;
          });

          return whiteboxes;
        })
      )
    }
  }

  private _mergeWhiteboxes(existingWhitebox: Whitebox, updatedWhitebox: Whitebox): Whitebox {
    return {
      ...existingWhitebox,
      ...updatedWhitebox,
      configuration: updatedWhitebox?.configuration ?? existingWhitebox?.configuration,
    };
  }

}
